<template>
  <div class="report-workspace" :aria-busy="!loaded || reportAccountLoading">
    <header class="report-heading">
      <div><p class="report-eyebrow">KONFIGURASI LAPORAN</p><h2>Pengaturan laporan</h2><p>{{ company.name || 'Perusahaan' }} &middot; Tahun buku {{ selectedYear }}</p></div>
      <vs-button type="border" to="/manage-coa">Kelola kategori COA</vs-button>
    </header>
    <div v-if="!company.id" class="report-state">Pilih perusahaan aktif untuk mengatur laporan.</div>
    <template v-else>
      <section class="report-guide" aria-label="Perbedaan pengaturan">
        <div><span class="report-step">1</span><h3>Klasifikasikan di Manage COA</h3><p>Tentukan kategori, saldo normal, dan identitas akun. Ini menjadi dasar pengelompokan dan perhitungan laporan.</p></div>
        <div><span class="report-step">2</span><h3>Susun di Pengaturan Laporan</h3><p>Pilih akun yang masuk laporan, petakan bagian Laba Rugi, dan tentukan akun ekuitas tujuan laba.</p></div>
      </section>
      <section class="report-section">
        <div class="report-section-heading"><div><h3>Susunan akun laporan</h3><p>Kategori Beban di COA masih perlu dibagi ke Beban Penjualan atau Administrasi &amp; Umum di sini.</p></div><vs-button v-if="canManageFinancialSettings" to="/report-settings/add" type="border" icon-pack="feather" icon="icon-plus">Tambah laporan</vs-button></div>
        <div v-if="!loaded" class="report-state" role="status">Memuat pengaturan laporan...</div>
        <div v-else-if="loadError" class="report-state report-error" role="alert">{{ loadError }} <button @click="getReportSetings">Coba lagi</button></div>
        <div v-else-if="!reportData.length" class="report-state">Belum ada pengaturan laporan untuk perusahaan ini.</div>
        <article v-for="report in reportData" v-else :key="report.id" class="report-card">
          <div class="report-card-heading"><div><span class="report-badge">{{ reportType(report) }}</span><h4>{{ report.name }}</h4><p>{{ reportDescription(report) }}</p></div><vs-button v-if="canManageFinancialSettings" size="small" @click="editRecord(report)">Atur akun{{ report.report_code === 'INCOME_STATEMENT' ? ' & bagian' : '' }}</vs-button></div>
          <details class="report-details"><summary>{{ (report.accounts || []).length }} akun dalam susunan laporan <span>Lihat akun</span></summary><div class="report-account-preview"><table><thead><tr><th>Kode akun</th><th>Nama akun</th></tr></thead><tbody><tr v-for="account in report.accounts" :key="account.id"><td>{{ account.account_code || '?' }}</td><td :class="{ 'report-parent': Number(account.level) < 3 }">{{ account.name }}</td></tr><tr v-if="!(report.accounts || []).length"><td colspan="2">Belum ada akun dipilih. Buka Atur akun untuk melengkapi.</td></tr></tbody></table></div></details>
          <div v-if="canManageFinancialSettings" class="report-card-footer"><button class="report-danger" @click="confirmDeleteRecord(report)">{{ isStandard(report) ? 'Reset pemetaan' : 'Hapus laporan' }}</button></div>
        </article>
      </section>
      <section v-if="canManageFinancialSettings" class="report-section">
        <div class="report-section-heading"><div><h3>Akun laba pada Neraca</h3><p>Berlaku untuk perusahaan dan tahun aktif. Kedua tujuan harus menggunakan akun ekuitas yang berbeda.</p></div></div>
        <div v-if="reportAccountLoading" class="report-state" role="status">Memuat akun ekuitas...</div>
        <div v-if="accountLoadError" class="report-state report-error" role="alert">Akun tujuan laba gagal dimuat. <button @click="getReportAccountSettings">Coba lagi</button></div>
        <p v-if="!reportAccountLoading && !accountLoadError && !reportAccounts.length" class="report-state">Belum ada akun ekuitas yang tersedia. Lengkapi kategori Ekuitas di <router-link to="/manage-coa">Manage COA</router-link>.</p>
        <div class="report-account-grid">
          <div v-for="role in accountRoles" :key="role.code" class="report-card">
            <span class="report-badge" :class="{ 'report-pending': !savedAccountSettings[role.field] }">{{ savedAccountSettings[role.field] ? 'Sudah diatur' : 'Belum diatur' }}</span>
            <h4>{{ role.title }}</h4><p>{{ role.description }}</p>
            <label :for="role.code">Akun ekuitas tujuan</label>
            <select :id="role.code" v-model="reportAccountSettings[role.field]" :disabled="reportAccountLoading || accountLoadError || !!savingRole || !reportAccounts.length">
              <option value="">Belum dipilih</option><option v-for="account in reportAccounts" :key="account.id" :value="account.id" :disabled="Number(account.id) === Number(reportAccountSettings[role.other])">{{ account.account_code }} - {{ account.name }}</option>
            </select>
            <div class="report-save"><vs-button size="small" :disabled="reportAccountLoading || accountLoadError || !!savingRole || !accountChanged(role.field)" @click="saveReportAccount(role.code, role.field)">{{ savingRole === role.code ? 'Menyimpan...' : 'Simpan akun tujuan' }}</vs-button><small v-if="accountChanged(role.field)">Belum disimpan</small></div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>

export default {
  data () {
    return {

      loaded: false,
      loadError: '',
      accountLoadError: false,
      savedAccountSettings: {},
      accountRoles: [
        { code: 'current_year_profit', field: 'current_year_profit_account_id', other: 'retained_earnings_account_id', title: 'Laba / rugi tahun berjalan', description: 'Menampilkan hasil perhitungan laba / rugi tahun aktif pada Neraca.' },
        { code: 'retained_earnings', field: 'retained_earnings_account_id', other: 'current_year_profit_account_id', title: 'Laba / rugi tahun sebelumnya', description: 'Menampung akumulasi laba / rugi tahun sebelumnya melalui sinkronisasi saldo awal atau tutup buku.' }
      ],
      reportData: [],
      reportAccounts: [],
      reportAccountLoading: false,
      savingRole: '',
      reportAccountSettings: {
        current_year_profit_account_id: '',
        retained_earnings_account_id: ''
      }
    }
  },
  computed: {
    selectedYear () { return localStorage.getItem('selectedYear') || '?' },
    company () {
      return JSON.parse(localStorage.getItem('activeCompany') || 'null') || {}
    },
    canManageFinancialSettings () {
      const user = JSON.parse(localStorage.getItem('userInfo') || '{}')
      return [0, 1].includes(Number(user.role_id))
    }
  },
  methods: {
    isStandard (report) { return ['BALANCE_SHEET', 'INCOME_STATEMENT'].includes(report.report_code) },
    reportType (report) { return ({ BALANCE_SHEET: 'Neraca', INCOME_STATEMENT: 'Laba Rugi' })[report.report_code] || 'Laporan tambahan' },
    reportDescription (report) {
      if (report.report_code === 'INCOME_STATEMENT') return 'Setiap akun transaksi Laba Rugi harus dipetakan tepat ke satu bagian agar laporan dapat dihitung.'
      if (report.report_code === 'BALANCE_SHEET') return 'Pilih susunan akun Neraca. Kelompok aset, liabilitas, dan ekuitas mengikuti kategori COA.'
      return 'Atur pilihan akun yang disertakan dalam laporan ini.'
    },
    accountChanged (field) { return String(this.reportAccountSettings[field] || '') !== String(this.savedAccountSettings[field] || '') },
    onLoaded () { this.loaded = true },
    confirmDeleteRecord (data) {
      if (!this.canManageFinancialSettings) return

      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.isStandard(data) ? 'Reset pemetaan laporan?' : 'Hapus laporan?',
        text: `Pemetaan akun "${data.name}" untuk perusahaan aktif akan dihapus. Laporan perlu dikonfigurasi kembali. Data akun dan transaksi tetap tersimpan.`,
        accept: () => {
          this.$store.dispatch('reportSettings/deleteReportSettings', {
            report_id: data.id,
            company_id: this.company.id
          })
            .then(() => {
              this.$vs.notify({
                color: 'success',
                title: 'Pengaturan dihapus',
                text: 'Pemetaan akun laporan untuk perusahaan aktif telah dihapus.'
              })
              return this.getReportSetings()
            }).catch(err => {
              this.$vs.notify({
                color: 'danger',
                title: 'Opps..',
                text: err
              })
            })
        },
        acceptText: this.isStandard(data) ? 'Reset pemetaan' : 'Hapus'
      })
    },
    editRecord (data) {
      if (!this.canManageFinancialSettings) return

      this.$router.push(`/report-settings/edit/${data.id}`).catch(() => {})
    },
    getReportSetings () {
      if (!this.company.id) { this.loaded = true; return }
      this.loaded = false
      this.loadError = ''
      return this.$store.dispatch('reportSettings/fetchReportSettings')
        .then(response => {
          this.reportData = response.data.data || []
        }).catch(err => {
          this.loadError = 'Pengaturan laporan gagal dimuat.'
          this.$vs.notify({
            color: 'danger',
            title: 'Opps..',
            text: err
          })
        })
        .finally(() => {
          this.onLoaded()
        })
    },
    getReportAccountSettings () {
      if (!this.canManageFinancialSettings || !this.company.id) return
      this.accountLoadError = false
      this.reportAccountLoading = true
      return this.$http.get(`report-account-settings/${this.company.id}`)
        .then(response => {
          const data = response.data.data || {}
          this.reportAccounts = data.accounts || []
          this.reportAccountSettings = Object.assign({
            current_year_profit_account_id: '',
            retained_earnings_account_id: ''
          }, data.settings || {})
          this.accountRoles.forEach(role => { this.reportAccountSettings[role.field] = this.reportAccountSettings[role.field] || '' })
          this.savedAccountSettings = Object.assign({}, this.reportAccountSettings)
        })
        .catch(error => { this.accountLoadError = true; this.notifyAccountSettingError(error) })
        .finally(() => { this.reportAccountLoading = false })
    },
    saveReportAccount (role, field) {
      if (!this.canManageFinancialSettings || this.savingRole || this.reportAccountLoading || this.accountLoadError || !this.accountChanged(field)) return
      this.savingRole = role
      this.$http.put(`report-account-settings/${this.company.id}/${role}`, {
        account_id: this.reportAccountSettings[field] || null
      }).then(() => {
        this.$vs.notify({ color: 'success', title: 'Berhasil', text: 'Pengaturan akun Neraca tersimpan.' })
        this.$set(this.savedAccountSettings, field, this.reportAccountSettings[field])
      }).catch(error => this.notifyAccountSettingError(error))
        .finally(() => { this.savingRole = '' })
    },
    notifyAccountSettingError (error) {
      const response = error && error.response && error.response.data
      const errors = response && response.errors
      const first = errors && Object.keys(errors).length ? errors[Object.keys(errors)[0]][0] : null
      this.$vs.notify({ color: 'danger', title: 'Gagal', text: first || (response && response.message) || 'Pengaturan akun laporan tidak dapat diproses.' })
    }
  },
  created () {
    this.getReportSetings()
    this.getReportAccountSettings()
  }
}

</script>

<style scoped>
.report-workspace { color: #31415a; }
.report-heading, .report-section-heading, .report-card-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; }
.report-heading { margin-bottom: 24px; align-items: center; }
.report-workspace h2 { font-size: 25px; color: #24344d; margin: 0; }
.report-workspace h3 { font-size: 16px; color: #24344d; margin: 0; }
.report-workspace h4 { font-size: 16px; color: #24344d; margin: 12px 0 8px; }
.report-workspace p { font-size: 13px; line-height: 1.65; color: #65748c; margin: 8px 0 0; }
.report-workspace .report-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 1.5px; color: var(--app-primary); margin: 0 0 8px; }
.report-guide, .report-account-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
.report-guide { padding: 22px; background: var(--app-primary-soft); border: 1px solid var(--app-primary-soft); border-radius: 10px; }
.report-step { float: left; margin-right: 10px; border-radius: 50%; background: #fff; color: var(--app-primary-ink); width: 25px; height: 25px; text-align: center; line-height: 25px; font-weight: 600; }
.report-section { margin-top: 28px; }
.report-section-heading { margin-bottom: 16px; }
.report-card { background: #fff; border: 1px solid #e0e6ee; border-radius: 10px; padding: 20px; margin-bottom: 12px; min-width: 0; }
.report-badge { display: inline-block; font-size: 11px; padding: 4px 9px; background: var(--app-primary-soft); color: var(--app-primary-ink); border-radius: 5px; }
.report-pending { background: var(--app-secondary-soft); color: var(--app-secondary-ink); }
.report-details { border-top: 1px solid #e8edf4; margin-top: 18px; padding-top: 14px; }
.report-details summary { cursor: pointer; font-size: 12px; }
.report-details summary span { color: var(--app-primary-ink); margin-left: 12px; }
.report-account-preview { max-height: 320px; overflow: auto; margin-top: 12px; }
.report-account-preview table { width: 100%; border-collapse: collapse; font-size: 12px; }
.report-account-preview th, .report-account-preview td { padding: 12px; border-bottom: 1px solid #e8edf4; text-align: left; }
.report-account-preview th { background: #f6f8fb; position: sticky; top: 0; }
.report-parent { font-weight: 600; }
.report-card-footer { text-align: right; margin-top: 12px; }
.report-workspace button.report-danger { color: var(--app-secondary-ink); }
.report-card-footer button, .report-state button { background: transparent; border: 0; padding: 5px; cursor: pointer; color: var(--app-primary-ink); font-size: 12px; }
.report-card label { display: block; font-size: 12px; font-weight: 600; margin: 20px 0 8px; }
.report-card select { width: 100%; min-height: 42px; border: 1px solid #bac5d5; background: #fff; border-radius: 6px; padding: 9px 10px; color: #31415a; }
.report-save { display: flex; align-items: center; gap: 12px; margin-top: 16px; flex-wrap: wrap; }
.report-save small { color: var(--app-secondary-ink); }
.report-state { padding: 22px; background: #fff; border: 1px solid #e0e6ee; border-radius: 8px; margin-bottom: 16px; }
.report-error { color: var(--app-secondary-ink); }
.report-workspace select:disabled { opacity: .6; }
.report-workspace button:focus-visible, .report-workspace select:focus-visible, .report-workspace summary:focus-visible { outline: 3px solid var(--app-primary); outline-offset: 3px; }
@media (max-width: 760px) { .report-guide, .report-account-grid { grid-template-columns: 1fr; } .report-heading, .report-section-heading, .report-card-heading { flex-direction: column; gap: 12px; } .report-card { padding: 16px; } }
</style>
